import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Angular Material Modules
import { A11yModule } from '@angular/cdk/a11y';

import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { PartnersComponent } from './components/partners/partners.component';
import { AddPartnerComponent } from './components/add-partner/add-partner.component';
import { PartnerDetailsComponent } from './components/partner-details/partner-details.component';
import { EditPartnerComponent } from './components/edit-partner/edit-partner.component';
import { CaseDetailsComponent } from './components/case-details/case-details.component';
import { KeycloakService } from '../services/keycloak.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { CasesComponent } from './components/cases/cases.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { SearchableSelectionComponent } from './components/ui/searchable-selection/searchable-selection.component';
import { UsageComponent } from './components/usage/usage.component';
import { FeatureUsageComponent } from './components/feature-usage/feature-usage.component';
import { PaginationComponent } from './components/ui/pagination/pagination.component';
import { EmptyStateComponent } from './components/ui/empty-state/empty-state.component';
import { ConfirmDialogComponent } from './components/ui/confirm-dialog/confirm-dialog.component';
import { CardTableDirective } from './directives/card-table.directive';
import { AppNavComponent } from './components/shell/app-nav/app-nav.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { ShipmentSmsComponent } from './components/shipment-sms/shipment-sms.component';
import { FeatureFlagsComponent } from './components/feature-flags/feature-flags.component';
import { TenantImportComponent } from './components/tenant-import/tenant-import.component';
import { CatalogsComponent } from './components/catalogs/catalogs.component';
import { CatalogDetailComponent } from './components/catalog-detail/catalog-detail.component';
import { CatalogFormDialogComponent } from './components/catalog-form-dialog/catalog-form-dialog.component';
import { PlansComponent } from './components/plans/plans.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { LegalDocumentsComponent } from './components/legal-documents/legal-documents.component';
import { LegalDocumentDetailComponent } from './components/legal-document-detail/legal-document-detail.component';
import { LegalDocumentFormDialogComponent } from './components/legal-document-form-dialog/legal-document-form-dialog.component';
import { MarkdownPipe } from './pipes/markdown.pipe';
import { ManufacturersComponent } from './components/manufacturers/manufacturers.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { environment } from '../environments/environment';

export function initializeKeycloak(keycloak: KeycloakService) {
  return () => keycloak.init();
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    PartnersComponent,
    AddPartnerComponent,
    PartnerDetailsComponent,
    EditPartnerComponent,
    CaseDetailsComponent,
    CasesComponent,
    FileUploadComponent,
    AccessDeniedComponent,
    SearchableSelectionComponent,
    UsageComponent,
    FeatureUsageComponent,
    PaginationComponent,
    EmptyStateComponent,
    ConfirmDialogComponent,
    CardTableDirective,
    AppNavComponent,
    NotFoundComponent,
    ShipmentSmsComponent,
    FeatureFlagsComponent,
    TenantImportComponent,
    CatalogsComponent,
    CatalogDetailComponent,
    CatalogFormDialogComponent,
    PlansComponent,
    SubscriptionsComponent,
    LegalDocumentsComponent,
    LegalDocumentDetailComponent,
    LegalDocumentFormDialogComponent,
    MarkdownPipe,
    ManufacturersComponent
  ],
  imports: [
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NgxChartsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),

    // Angular Material Modules
    A11yModule,
    MatIconModule,
    MatSnackBarModule,
    MatTabsModule,
    MatTooltipModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
